import React, { createContext, useContext, useState, useCallback } from 'react';
import { Document } from '../types/document';

interface DocumentContextType {
  documents: Document[];
  setDocuments: React.Dispatch<React.SetStateAction<Document[]>>;
  addDocuments: (newDocuments: Document[]) => void;
  removeDocument: (id: string) => void;
  updateDocumentProgress: (id: string, progress: number) => void;
}

const DocumentContext = createContext<DocumentContextType | undefined>(undefined);

export const DocumentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [documents, setDocuments] = useState<Document[]>([]);

  const addDocuments = useCallback((newDocuments: Document[]) => {
    setDocuments(prev => [...prev, ...newDocuments]);
  }, []);

  const removeDocument = useCallback((id: string) => {
    setDocuments(prev => prev.filter(doc => doc.id !== id));
  }, []);

  const updateDocumentProgress = useCallback((id: string, progress: number) => {
    setDocuments(prev => prev.map(doc =>
      doc.id === id ? { ...doc, progress } : doc
    ));
  }, []);

  return (
    <DocumentContext.Provider value={{
      documents,
      setDocuments,
      addDocuments,
      removeDocument,
      updateDocumentProgress,
    }}>
      {children}
    </DocumentContext.Provider>
  );
};

export const useDocumentContext = () => {
  const context = useContext(DocumentContext);
  if (!context) {
    throw new Error('useDocumentContext must be used within a DocumentProvider');
  }
  return context;
};